"use client";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { ErrorAlert } from "@/components/ui";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { fetchOTP, verifyOTP } from "@/lib/utils";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { Slot, FakeDash } from "@/components/ui/otpSlot";
import { PhoneIcon } from "@phosphor-icons/react";

export const PhoneValidation = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { setFormError, setPhoneVerified, setValidateFormDetails } = useFormStateStore();

	const { phone } = formValues;

	const [errors, setErrors] = useState({
		phone: false,
	});

	const [errorMsg, setErrMsg] = useState("");
	const [code, setCode] = useState("");
	const [txId, setTxId] = useState("");

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			phone: !/^04\d{8}$/.test(phone),
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [phone]);

	const sendOTP = useMutation({
		mutationKey: [phone],
		mutationFn: async () => {
			const res = await fetchOTP(phone);
			return res;
		},
		onSuccess: async (data) => {
			setTxId(data.id);
		},
		onError: async (err: any) => {
			setErrMsg(err.message);
		},
	});

	const confirmOTP = useMutation({
		mutationKey: [txId, code],
		mutationFn: async () => {
			if (!txId || !code) {
				throw new Error("OTP code is required for verification.");
			}
			const res = await verifyOTP(txId, code);
			return res;
		},
		onSuccess(data) {
			if (data.status == 200) {
				setPhoneVerified(true);
			}
		},
		onError: (err: any) => {
			setErrMsg(err.message);
		},
	});

	const handleChange = (code: string) => {
		setCode(code);
	};

	return (
		<div className="jF-py-4 jF-space-y-4">
			<div>
				<label
					htmlFor="phone-number"
					className="jF-block jF-text-sm jF-font-medium jF-leading-6 jF-text-gray-900"
				>
					Phone Number
				</label>
				<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
					<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
						<PhoneIcon
							weight="fill"
							className="jF-h-5 jF-w-5 jF-text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						name="phone-number"
						id="phone-number"
						placeholder="04XX-XXX-XXX"
						aria-describedby="phone-number"
						aria-label="Phone Number"
						inputMode="numeric"
						autoComplete="true"
						maxLength={10}
						autoFocus
						value={phone} // Ensure controlled component updates correctly
						onChange={(e) => {
							let value = e.target.value.replace(/[^0-9]/g, "");
							setFormValue("phone", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								phone: !/^04\d{8}$/.test(value) || value.trim() === "",
							}));
						}}
						onBlur={(e) => {
							let value = e.target.value.replace(/[^0-9]/g, "");
							setFormValue("phone", value);

							setErrors((prevErrors) => ({
								...prevErrors,
								phone: !/^04\d{8}$/.test(value) || value.trim() === "",
							}));
						}}
						className={clsx(
							errors.phone
								? "jF-ring-red-500 jF-ring-1 focus:jF-ring-red-500"
								: "focus:jF-ring-[--jF-theme-color]",
							"jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-10 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 placeholder:jF-text-gray-400 focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] sm:jF-text-md sm:jF-leading-6",
						)}
					/>
				</div>
				{errors.phone && phone.trim() === "" ? (
					<span className="jF-text-xs jF-text-red-500">Phone number is required</span>
				) : (
					errors.phone && (
						<span className="jF-text-xs jF-text-red-500">Incorrect format</span>
					)
				)}
			</div>

			<div className="jF-mt-5 sm:jF-mt-6">
				{sendOTP.isSuccess ? (
					""
				) : (
					<button
						className={clsx(
							sendOTP.isPending
								? "jF-bg-gray-200 hover:jF-cursor-not-allowed jF-text-gray-500 jF-ring-gray-100 hover:jF-bg-gray-200"
								: "jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color]",
							"jF-inline-flex jF-w-full jF-justify-center jF-rounded-md jF-px-3 jF-py-2 jF-text-md jF-font-semibold jF-text-white jF-shadow-sm jF-duration-200 focus-visible:jF-outline focus-visible:jF-outline-1 focus-visible:jF-outline-offset-1 focus-visible:jF-outline-gray-900",
						)}
						disabled={sendOTP.isPending}
						onClick={(e) => {
							// Verify phone number
							e.preventDefault();
							const isValid = validateForm();
							if (!isValid) return; // Stops next() from invoking
							if (phone && !errors.phone) {
								sendOTP.mutate();
							}
						}}
					>
						{sendOTP.isPending ? (
							<div
								role="status"
								className="jF-flex jF-flex-col jF-w-full jF-h-full jF-justify-center jF-items-center"
							>
								<svg
									aria-hidden="true"
									className="jF-w-8 jF-h-8 jF-text-[--jF-theme-color] jF-animate-spin jF-fill-white"
									viewBox="0 0 100 101"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
										fill="currentColor"
									/>
									<path
										d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
										fill="currentFill"
									/>
								</svg>
								<span className="jF-sr-only">Loading...</span>
							</div>
						) : (
							"Send Verification Code"
						)}
					</button>
				)}
			</div>
			{/* Verify Code */}
			{sendOTP.isError && <ErrorAlert text={errorMsg} />}
			{sendOTP.isSuccess && (
				<>
					<div>
						<label
							htmlFor="verifyOTP"
							className="jF-block jF-text-sm jF-font-medium jF-leading-6 jF-text-gray-900 jF-mb-1"
						>
							Verification Code
						</label>
						<div className="jF-group jF-flex jF-flex-row jF-items-center jF-justify-center">
							<OTPInput
								id="verifyOTP"
								onChange={handleChange}
								value={confirmOTP.error ? "" : code}
								maxLength={4}
								autoComplete="true"
								containerClassName="jF-text-gray-900 jF-group jF-flex jF-items-center has-[:disabled]:jF-opacity-30"
								render={({ slots }) => (
									<>
										<div className="jF-flex">
											{slots.slice(0, 2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>

										<FakeDash />

										<div className="jF-flex">
											{slots.slice(2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>
									</>
								)}
								inputMode="numeric"
								pattern={REGEXP_ONLY_DIGITS}
								autoFocus
							/>
						</div>
						{confirmOTP.isError && <ErrorAlert text={errorMsg} />}
					</div>
					<button
						className={clsx(
							confirmOTP.isPending
								? "jF-bg-gray-200 hover:jF-cursor-not-allowed jF-text-gray-500 jF-ring-gray-100 hover:jF-bg-gray-200"
								: "jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color]",
							"jF-inline-flex jF-w-full jF-justify-center jF-rounded-md jF-px-3 jF-py-2 jF-text-md jF-font-semibold jF-text-white jF-shadow-sm jF-duration-200 focus-visible:jF-outline focus-visible:jF-outline-2 focus-visible:jF-outline-offset-2 focus-visible:jF-outline-gray-900",
						)}
						type="button"
						disabled={confirmOTP.isPending}
						onClick={async (e) => {
							e.preventDefault();
							confirmOTP.error ? setCode("") : confirmOTP.mutate();
						}}
					>
						{confirmOTP.isPending ? (
							<div
								role="status"
								className="jF-flex jF-flex-col jF-w-full jF-h-full jF-justify-center jF-items-center"
							>
								<svg
									aria-hidden="true"
									className="jF-w-8 jF-h-8 jF-text-[--jF-theme-color] jF-animate-spin jF-fill-white"
									viewBox="0 0 100 101"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
										fill="currentColor"
									/>
									<path
										d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
										fill="currentFill"
									/>
								</svg>
								<span className="jF-sr-only">Loading...</span>
							</div>
						) : (
							"Verify Code"
						)}
					</button>
				</>
			)}
		</div>
	);
};
